import { useState, useEffect, useRef, useCallback } from "react";
import type { DashboardState, Task, WebSocketMessage } from "../types/dashboard";

/**
 * WebSocket hook with event sourcing for robust real-time updates
 *
 * Protocol:
 * 1. Connect WebSocket
 * 2. Receive welcome message with client_id
 * 3. Check for active runs via API
 * 4. If active run: fetch snapshot, then subscribe with since_seq
 * 5. Process events, track seq, detect gaps
 * 6. On reconnect: catchup from last known seq
 */

const getWebSocketURL = () => {
  if (typeof window === 'undefined') return 'ws://localhost:8765/ws';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
};

const WEBSOCKET_URL = getWebSocketURL();

const initialState: DashboardState = {
  run_id: "",
  state_version: 0,
  total_questions: 0,
  dataset_version: "",
  parallel_workers: 0,
  tasks: {},
  metrics: null,
  started_at: null,
  connected: false,
  traces: {},
  activeTraceView: null,
};

export function useWebSocket() {
  const [state, setState] = useState<DashboardState>(initialState);
  const [liveRunId, setLiveRunId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'live' | 'historical'>('live');

  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<number>();
  const viewModeRef = useRef<'live' | 'historical'>('live');
  const lastSeqRef = useRef<number>(0);
  const clientIdRef = useRef<string | null>(null);

  // Apply a snapshot from the API
  const applySnapshot = useCallback((snapshot: any) => {
    console.log(`📸 Applying snapshot for ${snapshot.run_id}, latest_seq=${snapshot.latest_seq}, tasks=${Object.keys(snapshot.tasks || {}).length}`);

    lastSeqRef.current = snapshot.latest_seq || 0;

    setState(prev => ({
      ...prev,
      run_id: snapshot.run_id,
      state_version: snapshot.latest_seq || 0,
      total_questions: snapshot.init_data?.total_questions || Object.keys(snapshot.tasks || {}).length,
      dataset_version: snapshot.init_data?.dataset_version || "",
      agent_path: snapshot.init_data?.agent_path,
      model: snapshot.init_data?.model || "",
      parallel_workers: snapshot.init_data?.parallel_workers || 0,
      started_at: snapshot.started_at,
      tasks: snapshot.tasks || {},
      metrics: snapshot.metrics || null,
    }));
  }, []);

  // Handle incoming message and track sequence
  const handleMessage = useCallback((message: WebSocketMessage) => {
    const seq = message.seq || 0;
    const expectedSeq = lastSeqRef.current + 1;

    // Log message
    const timestamp = new Date().toLocaleTimeString();
    switch (message.type) {
      case "welcome":
        console.log(`%c[${timestamp}] 🎉 WELCOME`, 'color: #22c55e', `client_id=${message.client_id}`);
        clientIdRef.current = message.client_id || null;
        return;

      case "subscribed":
        console.log(`%c[${timestamp}] 📡 SUBSCRIBED`, 'color: #3b82f6', `run_id=${message.run_id}, since_seq=${message.since_seq}`);
        return;

      case "ping":
        // Respond to server ping
        ws.current?.send(JSON.stringify({ type: "pong" }));
        return;

      case "pong":
        return;
    }

    // Gap detection for sequenced messages
    if (seq > 0 && seq > expectedSeq) {
      console.warn(`⚠️ Gap detected: expected seq=${expectedSeq}, got seq=${seq}`);
      // Could trigger catchup here, but for now just continue
    }

    // Update lastSeq
    if (seq > lastSeqRef.current) {
      lastSeqRef.current = seq;
    }

    // Log based on type
    switch (message.type) {
      case "init":
        console.log(`%c[${timestamp}] 🚀 INIT seq=${seq}`, 'color: #22c55e; font-weight: bold',
          `run=${message.data.run_id}, questions=${message.data.total_questions}`);
        break;
      case "task_update":
        const statusEmoji: Record<string, string> = { queued: '⏳', running: '▶️', completed: '✅', failed: '❌' };
        console.log(`%c[${timestamp}] ${statusEmoji[message.data.status] || '❓'} TASK seq=${seq}`,
          'font-weight: bold', `${message.data.question_id} → ${message.data.status}`);
        break;
      case "metrics":
        console.log(`%c[${timestamp}] 📊 METRICS seq=${seq}`, 'color: #8b5cf6',
          `completed=${message.data.completed}/${message.data.total}`);
        break;
      case "evaluation_complete":
        console.log(`%c[${timestamp}] 🏁 COMPLETE seq=${seq}`, 'color: #22c55e; font-weight: bold');
        break;
    }

    // Process message based on type
    switch (message.type) {
      case "init":
        setLiveRunId(message.data.run_id);
        if (viewModeRef.current !== 'historical') {
          setViewMode('live');
          viewModeRef.current = 'live';
          // Reset lastSeq for new run (sequences start from 1)
          lastSeqRef.current = seq;
          setState(prev => ({
            ...prev,
            run_id: message.data.run_id,
            state_version: seq,
            total_questions: message.data.total_questions,
            dataset_version: message.data.dataset_version,
            agent_path: message.data.agent_path,
            model: message.data.model,
            parallel_workers: message.data.parallel_workers,
            started_at: message.data.timestamp,
            tasks: {},
            metrics: null,
            traces: {},
          }));
          // Resubscribe to the new run so we get task_update messages
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({
              type: "subscribe",
              run_id: message.data.run_id,
              since_seq: seq
            }));
            console.log(`📡 Auto-subscribed to new run: ${message.data.run_id}`);
          }
        }
        break;

      case "task_update":
        if (viewModeRef.current !== 'live') return;
        const task: Task = message.data;
        setState(prev => ({
          ...prev,
          tasks: { ...prev.tasks, [task.question_id]: task },
          state_version: seq,
        }));
        break;

      case "metrics":
        if (viewModeRef.current !== 'live') return;
        setState(prev => ({ ...prev, metrics: message.data, state_version: seq }));
        break;

      case "evaluation_complete":
        console.log(`✅ Live run completed: ${message.data.run_id}`);
        setLiveRunId(null);
        viewModeRef.current = 'historical';
        setViewMode('historical');
        // Auto-load the completed run to show results immediately
        // Add 1 second delay to allow Firestore write to complete
        if (message.data.run_id) {
          setTimeout(() => {
          fetch(`/api/evaluation/runs/${message.data.run_id}`)
            .then(response => response.ok ? response.json() : null)
            .then(data => {
              if (data && viewModeRef.current === 'historical') {
                // Inline the loadHistoricalRunData logic
                const tasks = data.results ? data.results.reduce((acc: Record<string, any>, result: any) => {
                  acc[result.question_id] = result;
                  return acc;
                }, {}) : (data.tasks || {});

                const completedCount = data.results
                  ? data.results.filter((r: any) => r.status === 'completed' || r.status === 'success').length
                  : 0;

                const metrics = data.results ? {
                  scores: data.results.map((r: any) => r.accuracy_score || 0),
                  total: data.results.length,
                  completed: completedCount,
                  success: completedCount,
                  failed: data.results.filter((r: any) => r.status === 'failed' || r.status === 'error').length,
                  queued: 0,
                  running: 0,
                  sql_success_rate: data.results.length > 0
                    ? (data.results.filter((r: any) => r.sql_valid).length / data.results.length) * 100
                    : 0,
                  mean_accuracy: data.results.length > 0
                    ? data.results.reduce((sum: number, r: any) => sum + (r.accuracy_score || 0), 0) / data.results.length
                    : 0
                } : (data.metrics || null);

                setState(prev => ({
                  ...prev,
                  run_id: data.run_id,
                  total_questions: data.total_questions || data.results?.length || 0,
                  dataset_version: data.dataset_version,
                  agent_path: data.agent_path,
                  model: data.model,
                  parallel_workers: data.parallel_workers,
                  started_at: data.started_at,
                  tasks,
                  metrics,
                  traces: {},
                }));
              }
            })
            .catch(err => console.error("Failed to load completed run:", err));

            // Also trigger runs list refresh
            window.dispatchEvent(new CustomEvent('evaluation-complete'));
          }, 1000); // 1 second delay for Firestore to sync
        }
        break;

      case "status_update":
        // Legacy status updates from polling - ignore if we have seq-based events
        break;
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    if (ws.current?.readyState === WebSocket.OPEN) return;

    try {
      const websocket = new WebSocket(WEBSOCKET_URL);

      websocket.onopen = async () => {
        console.log("🔌 WebSocket connected");
        setState(prev => ({ ...prev, connected: true }));

        // Wait for welcome message, then check for active runs
        // The welcome message will be handled by onmessage
      };

      websocket.onmessage = async (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          // Handle welcome message specially - trigger initialization
          if (message.type === "welcome") {
            handleMessage(message);
            await initializeAfterConnect();
            return;
          }

          handleMessage(message);
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
        }
      };

      websocket.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      websocket.onclose = () => {
        console.log("❌ WebSocket disconnected");
        setState(prev => ({ ...prev, connected: false }));
        clientIdRef.current = null;

        // Auto-reconnect after 2 seconds
        reconnectTimeout.current = window.setTimeout(() => {
          console.log("🔄 Reconnecting...");
          connect();
        }, 2000);
      };

      ws.current = websocket;
    } catch (error) {
      console.error("Failed to connect WebSocket:", error);
    }
  }, [handleMessage]);

  // Initialize after WebSocket connects
  const initializeAfterConnect = useCallback(async () => {
    try {
      // Check for active runs
      console.log("🔍 Checking for active runs...");
      const activeResponse = await fetch('/api/evaluation/runs/active');

      if (activeResponse.ok) {
        const activeData = await activeResponse.json();
        console.log("📊 Active runs:", activeData);

        if (activeData.active_runs?.length > 0) {
          const activeRun = activeData.active_runs[0];
          console.log(`🔴 Found active run: ${activeRun.run_id}`);
          setLiveRunId(activeRun.run_id);

          // If not viewing historical, switch to live
          if (viewModeRef.current !== 'historical') {
            setViewMode('live');
            viewModeRef.current = 'live';

            // Fetch snapshot for the active run
            const snapshotResponse = await fetch(`/api/evaluation/runs/${activeRun.run_id}/snapshot`);
            if (snapshotResponse.ok) {
              const snapshot = await snapshotResponse.json();
              applySnapshot(snapshot);

              // Subscribe to live updates starting from snapshot's latest_seq
              ws.current?.send(JSON.stringify({
                type: "subscribe",
                run_id: activeRun.run_id,
                since_seq: snapshot.latest_seq || 0
              }));
            }
          }
          return;
        }
      }

      // No active run - load latest completed run
      console.log("ℹ️ No active runs, loading latest completed...");
      const latestResponse = await fetch('/api/evaluation/latest');
      if (latestResponse.ok) {
        const latestData = await latestResponse.json();
        if (latestData.run_id) {
          console.log(`📜 Loaded latest completed: ${latestData.run_id}`);
          loadHistoricalRunData(latestData);
          setViewMode('historical');
          viewModeRef.current = 'historical';
        }
      }

      // Subscribe to all future broadcasts (no specific run_id)
      ws.current?.send(JSON.stringify({
        type: "subscribe",
        since_seq: 0
      }));

    } catch (error) {
      console.error("Failed to initialize:", error);
    }
  }, [applySnapshot]);

  // Load historical run data from API response
  const loadHistoricalRunData = useCallback((data: any) => {
    const tasks = data.results ? data.results.reduce((acc: any, result: any) => {
      acc[result.question_id] = result;
      return acc;
    }, {}) : (data.tasks || {});

    const completedCount = data.results
      ? data.results.filter((r: any) => r.status === 'completed' || r.status === 'success').length
      : 0;

    const metrics = data.results ? {
      scores: data.results.map((r: any) => r.accuracy_score || 0),
      total: data.results.length,
      completed: completedCount,
      success: completedCount,
      failed: data.results.filter((r: any) => r.status === 'failed' || r.status === 'error').length,
      queued: 0,
      running: 0,
      sql_success_rate: data.results.length > 0
        ? (data.results.filter((r: any) => r.sql_valid).length / data.results.length) * 100
        : 0,
      mean_accuracy: data.results.length > 0
        ? data.results.reduce((sum: number, r: any) => sum + (r.accuracy_score || 0), 0) / data.results.length
        : 0
    } : (data.metrics || null);

    setState(prev => ({
      ...prev,
      run_id: data.run_id,
      total_questions: data.total_questions || data.results?.length || 0,
      dataset_version: data.dataset_version,
      agent_path: data.agent_path,
      model: data.model,
      parallel_workers: data.parallel_workers,
      started_at: data.started_at,
      tasks,
      metrics,
      traces: {},
    }));
  }, []);

  // Load a specific historical run
  const loadHistoricalRun = useCallback(async (runId: string) => {
    console.log(`📜 Loading historical run: ${runId}`);
    viewModeRef.current = 'historical';
    setViewMode('historical');

    try {
      const response = await fetch(`/api/evaluation/runs/${runId}`);
      if (response.ok) {
        const data = await response.json();
        if (viewModeRef.current === 'historical') {
          loadHistoricalRunData(data);
        }
      }
    } catch (error) {
      console.error("Failed to load historical run:", error);
    }
  }, [loadHistoricalRunData]);

  // Switch to live run
  const loadLiveRun = useCallback(async () => {
    console.log("🔴 Switching to live mode");
    viewModeRef.current = 'live';
    setViewMode('live');

    if (liveRunId) {
      // Fetch fresh snapshot
      try {
        const snapshotResponse = await fetch(`/api/evaluation/runs/${liveRunId}/snapshot`);
        if (snapshotResponse.ok) {
          const snapshot = await snapshotResponse.json();
          applySnapshot(snapshot);

          // Re-subscribe from current seq
          ws.current?.send(JSON.stringify({
            type: "subscribe",
            run_id: liveRunId,
            since_seq: snapshot.latest_seq || 0
          }));
        }
      } catch (error) {
        console.error("Failed to load live run:", error);
      }
    }
  }, [liveRunId, applySnapshot]);

  // Open trace viewer
  const openTraceViewer = useCallback(async (questionId: string) => {
    setState(prev => ({ ...prev, activeTraceView: questionId }));

    try {
      const response = await fetch(`/api/evaluation/traces/${state.run_id}/${questionId}`);
      if (response.ok) {
        const data = await response.json();
        setState(prev => ({
          ...prev,
          traces: { ...prev.traces, [questionId]: data.traces || [] }
        }));
      }
    } catch (error) {
      console.error("Failed to fetch traces:", error);
    }
  }, [state.run_id]);

  const closeTraceViewer = useCallback(() => {
    setState(prev => ({ ...prev, activeTraceView: null }));
  }, []);

  // Initialize on mount
  useEffect(() => {
    console.log("🚀 Initializing dashboard with event sourcing");
    connect();

    return () => {
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      if (ws.current) ws.current.close();
    };
  }, [connect]);

  // Handle tab visibility change - catchup when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && ws.current?.readyState === WebSocket.OPEN) {
        console.log("👁️ Tab became visible, checking for missed events...");

        if (liveRunId && viewModeRef.current === 'live') {
          try {
            // Fetch any events we might have missed
            const eventsResponse = await fetch(`/api/evaluation/runs/${liveRunId}/events?since_seq=${lastSeqRef.current}`);
            if (eventsResponse.ok) {
              const eventsData = await eventsResponse.json();
              if (eventsData.events?.length > 0) {
                console.log(`📬 Catching up ${eventsData.events.length} missed events`);
                for (const event of eventsData.events) {
                  handleMessage(event);
                }
              }
            }
          } catch (error) {
            console.error("Failed to catchup:", error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [liveRunId, handleMessage]);

  return {
    ...state,
    openTraceViewer,
    closeTraceViewer,
    loadHistoricalRun,
    loadLiveRun,
    liveRunId,
    viewMode
  };
}
